// pages/Privacy.jsx
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../global.css";
import "../styles/faqPage.css";
import ReviewsBar from "../components/ReviewsBar";

const Privacy = () => {
  const siteMode = process.env.REACT_APP_SITE_MODE || "ccg";
  const isPlanner = siteMode === "planner";
  const isMinibusGrab = siteMode === "minibusgrab";
  const isCoachGrab = siteMode === "cheapcoachgrab";
  const brandName = isMinibusGrab ? "MinibusGrab" : isCoachGrab ? "CheapCoachGrab" : "CheapCabGrab";
  const brandDomain = isMinibusGrab ? "www.minibusgrab.com" : isCoachGrab ? "www.cheapcoachgrab.com" : "www.cheapcabgrab.com";
  const brandService = isMinibusGrab
    ? "minibus hire, airport transfers and group travel"
    : isCoachGrab
      ? "coach hire, airport transfers, events and large group travel"
      : "taxi, minibus and coach transport services";
  const isPartnerBrand = isMinibusGrab || isCoachGrab;

  if (isPlanner) {
    return (
      <div className="faq-page">
        <Helmet>
          <title>Privacy Policy – Airport Journey Planner</title>
          <meta
            name="description"
            content="Airport Journey Planner privacy policy covering postcode, airport, flight number, contact, analytics and live journey planning data."
          />
          <link rel="canonical" href="https://www.airportjourneyplanner.com/privacy" />
          <meta property="og:title" content="Airport Journey Planner – Privacy Policy" />
          <meta
            property="og:description"
            content="How Airport Journey Planner uses data to provide live road, rail, flight and airport journey intelligence."
          />
          <meta property="og:url" content="https://www.airportjourneyplanner.com/privacy" />
        </Helmet>

        <div className="faq-grid">
          <div className="faq-left">
            <header className="faq-header">
              <h1>Privacy Policy</h1>
              <p>
                This policy explains how we use information when you use airport journey
                planning, flight checker and live travel intelligence tools.
              </p>
            </header>

            <div className="faq-section">
              <h2>1. Information We Collect</h2>
              <ul>
                <li><strong>Planner inputs:</strong> postcode, selected airport, date/time and optional flight number.</li>
                <li><strong>Live journey context:</strong> road, rail, flight, weather, parking and terminal status used to generate planner results.</li>
                <li><strong>Contact details:</strong> name, email, phone and message if you contact us.</li>
                <li><strong>Analytics:</strong> device, browser, approximate location, page usage and performance data.</li>
              </ul>
            </div>

            <div className="faq-section">
              <h2>2. How We Use Data</h2>
              <ul>
                <li>To provide airport journey planning and live status summaries.</li>
                <li>To pre-fill planner pages when you move from the homepage to an airport planner.</li>
                <li>To improve accuracy, reliability, safety and user experience.</li>
                <li>To respond to support requests sent to support@airportjourneyplanner.com.</li>
              </ul>
            </div>

            <div className="faq-section">
              <h2>3. Data Sources</h2>
              <p>
                The service combines internal planner logic with third-party and public data sources,
                including road, rail, flight, weather and airport operational feeds. These feeds may
                change, be delayed, or become temporarily unavailable.
              </p>
            </div>

            <div className="faq-section">
              <h2>4. Sharing</h2>
              <p>
                We do not sell personal data. We may use trusted processors for hosting, analytics,
                support email, maps, payments where relevant, and operational monitoring.
              </p>
            </div>

            <div className="faq-section">
              <h2>5. Retention</h2>
              <p>
                Planner and analytics records are retained only as needed for service operation,
                debugging, quality improvement and legal obligations. Contact correspondence may be
                retained for up to 18 months.
              </p>
            </div>

            <div className="faq-section">
              <h2>6. Your Rights</h2>
              <p>
                You can request access, correction or deletion of personal data by emailing{" "}
                <a className="faq-link" href="mailto:support@airportjourneyplanner.com">
                  support@airportjourneyplanner.com
                </a>.
              </p>
            </div>

            <div className="faq-section">
              <h2>7. Contact</h2>
              <p>
                For privacy questions, contact{" "}
                <a className="faq-link" href="mailto:support@airportjourneyplanner.com">
                  support@airportjourneyplanner.com
                </a>.
              </p>
              <p><strong>Last updated:</strong> May 2026</p>
            </div>

            <section className="faq-section" style={{ marginTop: "35px" }}>
              <h2>Useful Links</h2>
              <ul>
                <li><Link className="faq-link" to="/terms">Terms</Link></li>
                <li><Link className="faq-link" to="/contact">Contact</Link></li>
                <li><Link className="faq-link" to="/heathrow">Heathrow Planner</Link></li>
              </ul>
            </section>
          </div>

          <aside className="faq-right">
            <div className="faq-reviews-card">
              <h2>Privacy-first planning</h2>
              <p>Postcode and flight details are used to produce journey intelligence, not sold to third parties.</p>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-page">
      {/* ======================================================
          ⭐ SEO BLOCK — very important for compliance ranking
      ======================================================= */}
      <Helmet>
        <title>Privacy Policy – {brandName}</title>
        <meta
          name="description"
          content={
            isPartnerBrand
              ? `Read the ${brandName} Privacy Policy explaining what data we collect for ${brandService}, how we use it, retention periods, security, GDPR rights and support requests.`
              : "Read the CheapCabGrab Privacy Policy explaining what data we collect, how we use it, retention periods, security, GDPR rights, and how to request deletion or corrections."
          }
        />
        <link rel="canonical" href={`https://${brandDomain}/privacy`} />

        <meta
          name="keywords"
          content={
            isMinibusGrab
              ? "MinibusGrab privacy policy, minibus hire privacy, group travel data protection, GDPR minibus hire"
              : isCoachGrab
                ? "CheapCoachGrab privacy policy, coach hire privacy, group travel data protection, GDPR coach hire"
                : "cheapcabgrab privacy policy, taxi privacy, gdpr taxi service, data protection taxi, private hire privacy policy"
          }
        />

        <meta property="og:title" content={`${brandName} – Privacy Policy`} />
        <meta
          property="og:description"
          content={`Learn how ${brandName} collects, uses, stores and protects your personal data under GDPR and UK privacy law.`}
        />
        <meta property="og:url" content={`https://${brandDomain}/privacy`} />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "PrivacyPolicy",
            name: `${brandName} Privacy Policy`,
            url: `https://${brandDomain}/privacy`,
            provider: {
              "@type": "Organization",
              name: isPartnerBrand ? "CheapCabGrab" : brandName,
              url: isPartnerBrand ? "https://www.cheapcabgrab.com" : `https://${brandDomain}`,
            },
          })}
        </script>
      </Helmet>

      {/* ======================================================
          ⭐ TWO-COLUMN GRID (matches FAQ + Terms)
      ======================================================= */}
      <div className="faq-grid">
        {/* LEFT SIDE (main content) */}
        <div className="faq-left">
          <header className="faq-header">
            <h1>{isPartnerBrand ? "Privacy Policy" : "🔒 Privacy Policy"}</h1>
            <p>
              Your privacy is extremely important to us.  
              This policy explains what personal data {isPartnerBrand ? brandName : "CheapCabGrab"} collects, how we use it,
              and the rights you have under UK GDPR.
            </p>
          </header>

          {/* ======================================================
              ⭐ SECTIONS
          ======================================================= */}

          <div className="faq-section">
            <h2>1. Introduction</h2>
            {isPartnerBrand ? (
              <p>
                {brandName} (“we”, “our”, “us”) is committed to protecting your
                personal data. {brandName} is a brand front door using the CheapCabGrab
                booking and operations platform. We only collect information necessary to
                provide {brandService}, improve safety, and comply with licensing or
                operational requirements.
              </p>
            ) : (
              <p>
                CheapCabGrab (“we”, “our”, “us”) is committed to protecting your
                personal data. We only collect information necessary to provide taxi,
                minibus and coach transport services, improve safety, and comply with  
                private hire regulations.
              </p>
            )}
            <p>
              Certain details may be shared securely with licensed drivers, verified
              partners or payment processors (Stripe) strictly for booking fulfilment.
            </p>
          </div>

          <div className="faq-section">
            <h2>2. What Personal Data We Collect</h2>
            <p>We may collect the following categories:</p>
            <ul>
              <li>
                <strong>Analytics Data:</strong> IP address, device, browser type,
                session duration and navigation behaviour (Google Analytics).
              </li>
              <li>
                <strong>Customer Data:</strong> name, email, phone — used for booking
                and communication.
              </li>
              <li>
                <strong>Journey Data:</strong> pickup/drop-off, flight details,
                passenger name, passenger count, luggage and group details.
              </li>
              <li>
                <strong>Payment Data:</strong> card details processed via Stripe  
                ({isPartnerBrand ? "the CheapCabGrab platform" : "CheapCabGrab"} never stores card numbers).
              </li>
              <li>
                <strong>Correspondence:</strong> email, chat and phone records.
              </li>
            </ul>
          </div>

          <div className="faq-section">
            <h2>3. How We Use Your Data</h2>
            <ul>
              <li>✔ To manage bookings and dispatch drivers</li>
              <li>✔ To send confirmation emails and updates</li>
              <li>✔ To verify identity where required</li>
              <li>✔ To meet legal and licensing obligations</li>
              <li>✔ To improve website performance and safety</li>
            </ul>
          </div>

          <div className="faq-section">
            <h2>4. Data Retention Periods</h2>
            <ul>
              <li>Analytics data → retained indefinitely (anonymous)</li>
              <li>Customer data → 6 years (legal requirement)</li>
              <li>Journey data → 1 year (private hire rules)</li>
              <li>Transaction records → up to 6 years (financial audits)</li>
              <li>Correspondence → up to 18 months</li>
            </ul>
          </div>

          <div className="faq-section">
            <h2>5. Your GDPR Rights</h2>
            <p>You may request at any time:</p>
            <ul>
              <li>📄 A copy of your data (Data Access Request)</li>
              <li>✏️ Correction of inaccurate information</li>
              <li>🗑️ Deletion (“Right to Erasure”)</li>
              <li>⛔ To restrict or object to processing</li>
              <li>🔄 To withdraw consent (if applicable)</li>
            </ul>
            <p>
              We respond to all valid requests within 30 days.  
              Identity verification may be required.
            </p>
          </div>

          <div className="faq-section">
            <h2>6. Legal Basis for Processing</h2>
            <ul>
              <li><strong>Contract:</strong> providing your booked journey</li>
              <li><strong>Legal obligation:</strong> licensing & tax compliance</li>
              <li><strong>Legitimate interest:</strong> service improvement & safety</li>
            </ul>
          </div>

          <div className="faq-section">
            <h2>7. Data Sharing</h2>
            <p>Data may be shared with:</p>
            <ul>
              <li>✔ Licensed drivers completing your journey</li>
              {isPartnerBrand ? <li>✔ CheapCabGrab operations and approved partner operators</li> : null}
              <li>✔ Stripe (payment processing)</li>
              <li>✔ Support platforms (email/chat)</li>
            </ul>
            <p>
              We never sell or rent personal data to third parties.
            </p>
          </div>

          <div className="faq-section">
            <h2>8. Updates to This Policy</h2>
            <p>
              This Privacy Policy is reviewed regularly and updated when necessary.
              Last updated: <strong>October 2025</strong>.
            </p>
          </div>

          <section className="faq-section" style={{ marginTop: "35px" }}>
            <h2>Useful Links</h2>
            <ul>
              <li><Link className="faq-link" to="/terms">Terms & Conditions</Link></li>
              <li><Link className="faq-link" to="/contact">Contact Us</Link></li>
              {!isMinibusGrab && !isCoachGrab ? <li><Link className="faq-link" to="/faq">FAQs</Link></li> : null}
            </ul>
          </section>
        </div>

        {/* RIGHT SIDE */}
        <aside className="faq-right">
          <div className="faq-reviews-card">
            <h2>{brandName} privacy</h2>
            <p>
              {isPartnerBrand
                ? "Brand-specific enquiries using the shared CheapCabGrab operations platform."
                : "Your privacy matters. Your trust matters even more."}
            </p>
            <div style={{ marginTop: "10px" }}>
              <ReviewsBar />
            </div>
          </div>
        </aside>
      </div>

      {/* Floating CTA */}
      <button
        className="floating-book-btn"
        onClick={() => (window.location.href = "/")}
      >
        {isMinibusGrab ? "Get Minibus Quote" : isCoachGrab ? "Get Coach Quote" : "🚖 Book Now"}
      </button>
    </div>
  );
};

export default Privacy;
